import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import HeaderBar from "../components/HeaderBar";
import MenuBar from "../components/MenuBar";
import { db } from "../firebase";
import { WORKFLOW, getWorkflowStatus } from "../utils/pendingOrderWorkflow";

function normalizeName(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .replace(/unit0+(\d+)/g, "unit$1");
}

function toJsDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") {
    const d = v.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === "string" || typeof v === "number") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function parseAmount(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = parseFloat(String(v ?? "").trim());
  return Number.isFinite(n) ? n : 0;
}

function isCancelledOrder(o) {
  // Orders appear to store cancellation in `status` (contains "cancel").
  return String(o?.status || o?.workflowStatus || "").toLowerCase().includes("cancel");
}

function formatINR(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

export default function PartyOrderHistoryPage() {
  const { partyId } = useParams();
  const [partyName, setPartyName] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expandedOrderIds, setExpandedOrderIds] = useState([]);

  const toggleHistory = (id) => {
    setExpandedOrderIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const partySnap = await getDoc(doc(db, "parties", partyId));
        const currentPartyName = partySnap.exists() ? String(partySnap.data()?.partyName || "") : "";
        setPartyName(currentPartyName);

        const ordersSnap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
        const partyNorm = normalizeName(currentPartyName);
        const matchedOrders = ordersSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((o) => {
            const supplierNorm = normalizeName(o.supplier ?? o.party ?? o.vendor);
            return supplierNorm && partyNorm && supplierNorm === partyNorm;
          })
          .map((o) => {
            const orderDate = toJsDate(o.date ?? o.orderDate ?? o.createdAt);
            const receivedDate = toJsDate(o.receivedDate ?? o.arrivedDate);
            const workflowStatus = getWorkflowStatus(o);
            const cancelled = isCancelledOrder(o);
            const amount = parseAmount(o.orderAmount ?? o.amount);
            const daysToArrive =
              orderDate && receivedDate
                ? Math.max(0, Math.floor((receivedDate.getTime() - orderDate.getTime()) / 86400000))
                : null;
            const daysOpen = orderDate
              ? Math.max(
                  0,
                  Math.floor(((receivedDate || new Date()).getTime() - orderDate.getTime()) / 86400000)
                )
              : 0;
            return { ...o, orderDate, receivedDate, workflowStatus, cancelled, amount, daysToArrive, daysOpen };
          });
        setOrders(matchedOrders);
      } catch (e) {
        setErr(e?.message || "Failed to load party order history.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [partyId]);

  const summary = useMemo(() => {
    const nonCancelled = orders.filter((o) => !o.cancelled);
    const received = nonCancelled.filter((o) => o.workflowStatus === WORKFLOW.RECEIVED);
    const open = nonCancelled.filter((o) => o.workflowStatus !== WORKFLOW.RECEIVED);
    return { total: orders.length, pending: open.length, received: received.length };
  }, [orders]);

  return (
    <div className="min-h-screen bg-gray-100">
      <MenuBar />
      <HeaderBar title={`Order History - ${partyName || partyId}`} />
      <main className="mx-auto w-[95%] py-4">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-xl border bg-white p-4 shadow">
          <div>
            <Link to="/pending-orders" className="text-sm font-semibold text-indigo-600 hover:underline">
              ← Back to Pending Orders
            </Link>
            <h1 className="mt-2 text-lg font-semibold text-slate-900">Party Order History</h1>
            <p className="text-xs text-slate-600">{partyName || partyId}</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs font-semibold">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">Total {summary.total}</span>
            <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">Open {summary.pending}</span>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">Received {summary.received}</span>
          </div>
        </div>

        {err ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div> : null}

        {loading ? (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-600 shadow">Loading order history...</div>
        ) : orders.length === 0 ? (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-600 shadow">No pending order history found for this party.</div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {o.brand || "Order"} {o.quantity ? `· Qty ${o.quantity}` : ""}
                    </p>
                    <p className="text-xs text-slate-600">
                      Supplier: {o.supplier || partyName || "—"} {o.sendType ? `| Type: ${o.sendType}` : ""}{" "}
                      {o.preparedBy ? `| Prepared: ${o.preparedBy}` : ""}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p
                      className={[
                        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                        o.cancelled
                          ? "bg-rose-50 text-rose-900 border-rose-200"
                          : o.workflowStatus === WORKFLOW.RECEIVED
                          ? "bg-emerald-50 text-emerald-900 border-emerald-200"
                          : "bg-amber-50 text-amber-900 border-amber-200",
                      ].join(" ")}
                    >
                      {o.cancelled ? "cancelled" : o.workflowStatus || o.status || "pending"}
                    </p>
                    <p className="text-xs text-slate-600">Order date: {o.orderDate ? o.orderDate.toLocaleDateString("en-IN") : "—"}</p>
                    <p className="text-xs text-slate-600">Received: {o.receivedDate ? o.receivedDate.toLocaleDateString("en-IN") : "—"}</p>
                    <div className="mt-1 space-y-0.5">
                      <p className="text-xs font-semibold text-slate-700">
                        Paid: <span className="font-semibold">{formatINR(o.amount)}</span>
                      </p>
                      <p className="text-xs font-semibold text-slate-700">
                        Received:{" "}
                        <span className="font-semibold">{o.workflowStatus === WORKFLOW.RECEIVED ? formatINR(o.amount) : "—"}</span>
                      </p>
                    </div>
                    {o.workflowStatus === WORKFLOW.RECEIVED && typeof o.daysToArrive === "number" ? (
                      <p className="text-xs font-semibold text-slate-800">{o.daysToArrive} days to arrive</p>
                    ) : (
                      <p className="text-xs font-semibold text-slate-800">{o.daysOpen} days open</p>
                    )}
                  </div>
                </div>

                {(() => {
                  const historyRows =
                    Array.isArray(o.statusHistory) && o.statusHistory.length
                      ? o.statusHistory
                      : [{ from: "-", to: o.workflowStatus || o.status || "submitted", at: o.createdAt, by: o.preparedBy || "-", comment: "" }];
                  const isExpanded = expandedOrderIds.includes(o.id);
                  const rowsToRender = isExpanded ? historyRows : historyRows.slice(-1);
                  return (
                    <>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold text-slate-500">
                          Status history: {historyRows.length} update{historyRows.length === 1 ? "" : "s"}
                        </p>
                        <button
                          type="button"
                          onClick={() => toggleHistory(o.id)}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-50"
                        >
                          {isExpanded ? "Hide" : "Show"} history
                        </button>
                      </div>
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="border px-2 py-1 text-left">From</th>
                              <th className="border px-2 py-1 text-left">To</th>
                              <th className="border px-2 py-1 text-left">At</th>
                              <th className="border px-2 py-1 text-left">By</th>
                              <th className="border px-2 py-1 text-left">Comment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rowsToRender.map((h, idx) => (
                              <tr key={`${o.id}_${idx}`} className="bg-white">
                                <td className="border px-2 py-1">{h.from || "—"}</td>
                                <td className="border px-2 py-1">{h.to || "—"}</td>
                                <td className="border px-2 py-1">{toJsDate(h.at)?.toLocaleString("en-IN") || "—"}</td>
                                <td className="border px-2 py-1">{h.by || h.byEmail || "—"}</td>
                                <td className="border px-2 py-1">{h.comment || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

